import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { useModal } from '../../context/ModalContext';
import { FiUsers, FiPlus, FiEdit, FiTrash2, FiX, FiCheck } from 'react-icons/fi';

function StaffManagement() {
  const { alert, confirm } = useModal();
  const [staff, setStaff] = useState([]);
  const [limitInfo, setLimitInfo] = useState({
    currentCount: 0,
    maxAllowed: 0,
    remaining: 0
  });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  useEffect(() => {
    fetchStaff();
    fetchLimitInfo();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/staff');
      setStaff(response.data.staff || []);
      setLimitInfo({
        currentCount: response.data.currentCount || 0,
        maxAllowed: response.data.maxAllowed || 0,
        remaining: response.data.remaining || 0
      });
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      await alert(error.response?.data?.error || 'Failed to load staff users', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLimitInfo = async () => {
    try {
      const response = await api.get('/api/staff/limit');
      setLimitInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch limit info:', error);
    }
  };

  const handleCreateStaff = async () => {
    try {
      if (!formData.name || !formData.email || !formData.password) {
        await alert('Please fill in all fields', 'Validation Error', 'warning');
        return;
      }

      if (formData.password.length < 6) {
        await alert('Password must be at least 6 characters long', 'Validation Error', 'warning');
        return;
      }

      await api.post('/api/staff', formData);
      await alert('Staff user created successfully!', 'Success', 'success');
      setShowCreateModal(false);
      resetForm();
      fetchStaff();
      fetchLimitInfo();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to create staff user', 'Error', 'error');
    }
  };

  const handleUpdateStaff = async () => {
    try {
      if (!formData.name || !formData.email) {
        await alert('Please fill in name and email', 'Validation Error', 'warning');
        return;
      }

      if (formData.password && formData.password.length < 6) {
        await alert('Password must be at least 6 characters long', 'Validation Error', 'warning');
        return;
      }

      await api.put(`/api/staff/${selectedStaff.id}`, formData);
      await alert('Staff user updated successfully!', 'Success', 'success');
      setShowEditModal(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update staff user', 'Error', 'error');
    }
  };

  const handleDeleteStaff = async (staffMember) => {
    try {
      const confirmed = await confirm(
        `Are you sure you want to delete ${staffMember.name}?`,
        'Delete Staff User',
        'warning'
      );

      if (!confirmed) return;

      await api.delete(`/api/staff/${staffMember.id}`);
      await alert('Staff user deleted successfully!', 'Success', 'success');
      fetchStaff();
      fetchLimitInfo();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to delete staff user', 'Error', 'error');
    }
  };

  const openEditModal = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormData({
      name: staffMember.name || '',
      email: staffMember.email || '',
      password: '' // Don't pre-fill password
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: ''
    });
    setSelectedStaff(null);
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Staff Management</h1>
          <p className="text-gray-600 mt-1">Manage staff logins for your restaurant</p>
        </div>

        {/* Limit Info Card */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Staff Login Limit</h2>
              <p className="text-gray-600">
                You have used <span className="font-bold text-gray-900">{limitInfo.currentCount}</span> of{' '}
                <span className="font-bold text-gray-900">{limitInfo.maxAllowed}</span> allowed staff logins
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{limitInfo.remaining}</div>
              <div className="text-sm text-gray-600">Remaining</div>
            </div>
          </div>
          {limitInfo.remaining === 0 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
              <strong>Limit Reached:</strong> You have reached the maximum number of staff logins. 
              Contact the owner to increase your limit or delete existing staff users to free up slots.
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mb-6 flex justify-between items-center">
          <div className="text-gray-600">
            Total Staff: <span className="font-semibold text-gray-900">{staff.length}</span>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            disabled={limitInfo.remaining === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
              limitInfo.remaining === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
          >
            <FiPlus />
            Add Staff
          </button>
        </div>

        {/* Staff List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-600">Loading staff...</div>
          ) : staff.length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              <FiUsers className="mx-auto text-4xl text-gray-400 mb-4" />
              <p>No staff users created yet</p>
              {limitInfo.remaining > 0 && (
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}
                  className="mt-4 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Create First Staff User
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Last Login</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {staff.map((staffMember) => (
                    <tr key={staffMember.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{staffMember.name}</div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{staffMember.email}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          staffMember.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {staffMember.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">
                        {staffMember.last_login
                          ? new Date(staffMember.last_login).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(staffMember)}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit Staff"
                          >
                            <FiEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteStaff(staffMember)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete Staff"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Staff Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Create Staff User</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Staff Name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="staff@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Minimum 6 characters"
                />
                <p className="text-xs text-gray-500 mt-1">Password must be at least 6 characters long</p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateStaff}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Create Staff
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Edit Staff User</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Staff Name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="staff@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">New Password (leave blank to keep current)</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Leave blank to keep current password"
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank to keep current password. Minimum 6 characters if changing.</p>
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedStaff.is_active}
                    onChange={(e) => setSelectedStaff({ ...selectedStaff, is_active: e.target.checked })}
                    className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateStaff}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Update Staff
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default StaffManagement;
